// ─── Media upload slot with thumbnails, batch upload, drag & drop ───────────
// Used for images, logos, videos, banners. Min/max enforced.

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Upload, Trash2, Eye, X, ImageIcon, Play } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import toast from 'react-hot-toast';
import type { SlotConfig, MediaFileItem } from './types';
import type { Campaign } from '@/api/campaigns';
import type { GXMultiSearchResponse } from '@/api/gearExchange';
import { GearExchangeModal, type GXImportedFile } from '../../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../../gear-exchange/GearExchangeImportButton';

const NON_IMPORTABLE_SLOTS = new Set(['youtube_video', 'image_extension']);
const VIDEO_FALLBACK_SENTINEL = '__video_fallback__' as const;

export function MediaSlotSection({
  slot,
  files,
  onAddFiles,
  onRemoveFile,
  maxFiles = Infinity,
  issue,
  onDimensionsLoaded,
  fieldIssues,
  showErrors = false,
  showWarnings = false,
  gxData,
  gxLoading,
  gxError,
  campaign,
  campaignId,
  onUseDefault,
  readOnly = false,
}: {
  slot: SlotConfig;
  files: MediaFileItem[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  maxFiles?: number;
  issue?: { errors: string[]; warnings: string[] };
  onDimensionsLoaded?: (id: string, width: number, height: number) => void;
  fieldIssues?: Record<string, { errors: string[]; warnings: string[] }>;
  showErrors?: boolean;
  showWarnings?: boolean;
  gxData?: GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
  campaign?: Campaign | null;
  campaignId?: string;
  onUseDefault?: () => void;
  readOnly?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewItem, setPreviewItem] = useState<{ src: string; isVideo: boolean } | null>(null);
  const [showGxModal, setShowGxModal] = useState(false);
  const accept = slot.accept ?? 'image/*';

  const showImport = !NON_IMPORTABLE_SLOTS.has(slot.slot);

  const currentCount = files.filter((f) => f.file || f.saved).length;

  function handleGxImport(imported: GXImportedFile[]) {
    const remaining = maxFiles - currentCount;
    const toAdd = imported.slice(0, remaining).map((f) => f.file);
    if (toAdd.length > 0) onAddFiles(toAdd);
  }

  function validateAndAddFiles(rawFiles: File[]) {
    const accepted: File[] = [];
    const rejectedType: string[] = [];
    const rejectedSize: string[] = [];

    for (const file of rawFiles) {
      if (slot.acceptTypes && slot.acceptTypes.length > 0) {
        if (!slot.acceptTypes.includes(file.type)) {
          rejectedType.push(file.name);
          continue;
        }
      }
      if (slot.maxFileSize && file.size > slot.maxFileSize) {
        rejectedSize.push(file.name);
        continue;
      }
      accepted.push(file);
    }

    if (rejectedType.length > 0) {
      const types = slot.acceptTypes?.map((t) => t.split('/')[1]?.toUpperCase()).join(', ') ?? '';
      toast.error(
        `${rejectedType.length} file${rejectedType.length > 1 ? 's' : ''} rejected — only ${types} accepted`,
      );
    }
    if (rejectedSize.length > 0) {
      const maxKB = slot.maxFileSize ? slot.maxFileSize / 1024 : 0;
      const sizeLabel = maxKB >= 1024 ? `${Math.round(maxKB / 1024)}MB` : `${Math.round(maxKB)}KB`;
      toast.error(
        `${rejectedSize.length} file${rejectedSize.length > 1 ? 's' : ''} rejected — max ${sizeLabel} per file`,
      );
    }

    if (accepted.length > 0) {
      const currentCount = files.length;
      const remaining = maxFiles - currentCount;
      if (remaining <= 0) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }
      const toAdd = accepted.slice(0, remaining);
      if (toAdd.length < accepted.length) {
        toast.error(
          `Only ${toAdd.length} of ${accepted.length} files added — maximum ${maxFiles} reached`,
        );
      }
      onAddFiles(toAdd);
    }
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }
  function onDragLeave() {
    setIsDragging(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length) validateAndAddFiles(Array.from(e.dataTransfer.files));
  }

  return (
    <>
      <div
        className={`rounded-lg border p-3 ${issue?.errors.length ? 'border-red-300 bg-red-50/30' : issue?.warnings.length ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 bg-slate-50/50'}`}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-slate-600">
            {slot.label}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-1 text-[10px] font-medium text-slate-400 cursor-default">
                  ({slot.optional ? 'Optional' : 'Required'})
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {slot.optional
                  ? `This slot is optional and allows up to ${slot.max ?? 'unlimited'} asset${slot.max === 1 ? '' : 's'}`
                  : `This slot requires at least 1 asset, up to ${slot.max ?? 'unlimited'}`}
              </TooltipContent>
            </Tooltip>
            {slot.minLength && (
              <span className="ml-1 text-[10px] text-slate-400">· {slot.minLength}</span>
            )}
          </p>
          <div className="flex items-center gap-2 text-[10px] text-slate-400">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="tabular-nums cursor-default">
                  {slot.hideMax
                    ? `${currentCount} uploaded`
                    : `${currentCount} / ${slot.max ?? maxFiles}`}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {slot.hideMax
                  ? `${currentCount} asset${currentCount !== 1 ? 's' : ''} uploaded`
                  : currentCount === 0
                    ? `No assets uploaded yet — maximum ${slot.max ?? maxFiles}`
                    : `${currentCount} asset${currentCount !== 1 ? 's' : ''} uploaded out of a maximum of ${slot.max ?? maxFiles}`}
              </TooltipContent>
            </Tooltip>
            {!readOnly && onUseDefault && slot.defaultMedia && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onUseDefault}
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2 decoration-slate-300"
                  >
                    <ImageIcon className="w-3 h-3" />
                    Ford Logo
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="p-3 max-w-48">
                  <div className="flex flex-col items-center gap-2">
                    <img
                      src={`/api/static/${slot.defaultMedia.path}`}
                      alt="Default logo"
                      className="w-16 h-16 rounded object-cover border border-white/10"
                    />
                    <p className="text-xs text-center leading-snug">
                      Adds the default Ford logo. You can replace it anytime.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            {slot.dimensions ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="underline underline-offset-2 decoration-slate-300 cursor-default">
                    Specs
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-80 p-0">
                  <table className="text-xs">
                    <tbody>
                      {slot.dimensions.split('·').map((part, i) => {
                        const trimmed = part.trim();
                        if (!trimmed) return null;
                        const dashIdx = trimmed.indexOf('—');
                        if (dashIdx > 0) {
                          return (
                            <tr key={i} className="border-b border-white/5 last:border-0">
                              <td className="py-1 px-3 font-medium text-white whitespace-nowrap">
                                {trimmed.slice(0, dashIdx).trim()}
                              </td>
                              <td className="py-1 px-3 text-slate-300">
                                {trimmed.slice(dashIdx + 1).trim()}
                              </td>
                            </tr>
                          );
                        }
                        return (
                          <tr key={i} className="border-b border-white/5 last:border-0">
                            <td colSpan={2} className="py-1.5 px-3 text-slate-300">
                              {trimmed}
                            </td>
                          </tr>
                        );
                      })}
                      {slot.recommended && (
                        <tr className="border-b border-white/5 last:border-0">
                          <td className="py-1 px-3 font-medium text-white whitespace-nowrap">
                            Recommended
                          </td>
                          <td className="py-1 px-3 text-slate-300">{slot.recommended}</td>
                        </tr>
                      )}
                      {slot.optional && (
                        <tr className="border-b border-white/5 last:border-0">
                          <td colSpan={2} className="py-1.5 px-3 text-slate-300">
                            Optional
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={slot.acceptTypes?.join(',') ?? accept}
          multiple
          className="hidden"
          disabled={readOnly}
          onChange={(e) => {
            if (e.target.files?.length) validateAndAddFiles(Array.from(e.target.files));
            e.target.value = '';
          }}
        />

        {files.length === 0 ? (
          readOnly ? (
            <div
              className="h-16 w-full rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
            >
              <p className="text-xs text-slate-400">No assets uploaded</p>
            </div>
          ) : maxFiles > 0 ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`h-28 w-full rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 px-4 ${
                isDragging
                  ? 'border-[#0014cc]/50 bg-[#0014cc]/5'
                  : 'border-dashed border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isDragging ? 'bg-[#0014cc]/10' : 'bg-slate-100'}`}
              >
                <Upload
                  className={`w-4 h-4 transition-colors ${isDragging ? 'text-[#0014cc]' : 'text-slate-400'}`}
                />
              </div>
              <p className="text-xs text-slate-500 font-medium">
                {isDragging ? 'Drop to upload' : 'Click to upload or drag & drop'}
              </p>
            </button>
          ) : (
            <div className="h-16 w-full rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center">
              <p className="text-xs text-slate-400">Image limit reached</p>
            </div>
          )
        ) : (
          <div
            className={`overflow-x-auto min-w-0 max-w-full hover-scroll rounded-xl p-1.5 -m-1.5 transition-all ${
              isDragging ? 'bg-[#0014cc]/5 ring-2 ring-[#0014cc]/30' : ''
            }`}
            onDragOver={readOnly ? undefined : onDragOver}
            onDragLeave={readOnly ? undefined : onDragLeave}
            onDrop={readOnly ? undefined : onDrop}
          >
            <div className="flex gap-2" style={{ width: 'max-content' }}>
              {files.map((item) => {
                const ratioIssue = fieldIssues?.[`media_ratio_${item.id}`];
                return (
                  <SlotThumb
                    key={item.id}
                    item={item}
                    slotName={slot.slot}
                    onRemove={() => onRemoveFile(item.id)}
                    canRemove={!readOnly}
                    onPreview={setPreviewItem}
                    onDimensionsLoaded={onDimensionsLoaded}
                    ratioIssue={ratioIssue}
                    showErrors={showErrors}
                    showWarnings={showWarnings}
                  />
                );
              })}
              {!readOnly && files.length < maxFiles && (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className={`relative flex-shrink-0 h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all ${
                    isDragging
                      ? 'border-[#0014cc]/50 bg-[#0014cc]/10 text-[#0014cc]'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-400'
                  }`}
                  style={{ aspectRatio: SLOT_ASPECT[slot.slot] ?? '1/1' }}
                  title="Add more"
                >
                  <Plus className="w-5 h-5" />
                  <span className="text-[10px] font-medium">{isDragging ? 'Drop' : 'Add'}</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Per-file ratio errors/warnings */}
        {files.map((item) => {
          const ri = fieldIssues?.[`media_ratio_${item.id}`];
          if (!ri) return null;
          return (
            <div key={item.id}>
              {ri.errors.length > 0 &&
                showErrors &&
                ri.errors.map((msg, i) => (
                  <p key={`e${i}`} className="text-xs text-red-600 mt-1">
                    ✕ {msg}
                  </p>
                ))}
              {ri.warnings.length > 0 &&
                showWarnings &&
                ri.warnings.map((msg, i) => (
                  <p key={`w${i}`} className="text-xs text-amber-600 mt-1">
                    ⚠ {msg}
                  </p>
                ))}
            </div>
          );
        })}
      </div>

      {/* ── Gear Exchange Import ──────────────────────────────────── */}
      {showImport && !readOnly && (
        <div className="mt-2">
          <GearExchangeImportButton
            totalAssets={gxData?.total_assets ?? null}
            loading={gxLoading}
            error={gxError}
            fordshareIds={campaign?.fordshare_entries?.map((e) => e.id)}
            onClick={() => setShowGxModal(true)}
          />
        </div>
      )}

      {showGxModal && gxData && campaign && campaignId && (
        <GearExchangeModal
          data={gxData}
          campaign={campaign}
          campaignId={campaignId}
          formatLabel={slot.label}
          zoneLimits={[{ ratio: 'any', limit: slot.max ?? maxFiles, existing: currentCount }]}
          onClose={() => setShowGxModal(false)}
          onImport={handleGxImport}
          slotFilter={{
            acceptTypes: slot.acceptTypes,
            maxFileSize: slot.maxFileSize,
            dimensions: slot.dimensions,
            acceptedRatios: slot.acceptedRatios,
          }}
        />
      )}

      {previewItem &&
        createPortal(
          <MediaPreviewModal
            src={previewItem.src}
            isVideo={previewItem.isVideo}
            onClose={() => setPreviewItem(null)}
          />,
          document.body,
        )}
    </>
  );
}

// ─── Thumbnail aspect ratios ────────────────────────────────────────────────

const SLOT_ASPECT: Record<string, string> = {
  image: '1/1',
  landscape_image: '16/9',
  square_image: '1/1',
  portrait_image: '9/16',
  square_logo: '1/1',
  landscape_logo: '16/9',
  video: '1/1',
  companion_banner: '16/9',
};

type PreviewInfo = { src: string; isVideo: boolean };

function SlotThumb({
  item,
  slotName,
  onRemove,
  canRemove = true,
  onPreview,
  onDimensionsLoaded,
  ratioIssue,
  showErrors,
  showWarnings,
}: {
  item: MediaFileItem;
  slotName: string;
  onRemove: () => void;
  canRemove?: boolean;
  onPreview: (p: PreviewInfo) => void;
  onDimensionsLoaded?: (id: string, w: number, h: number) => void;
  ratioIssue?: { errors: string[]; warnings: string[] };
  showErrors?: boolean;
  showWarnings?: boolean;
}) {
  const aspect = SLOT_ASPECT[slotName] ?? '1/1';
  const reportDims = useCallback(
    (w: number, h: number) => onDimensionsLoaded?.(item.id, w, h),
    [onDimensionsLoaded, item.id],
  );
  const borderHighlight =
    ratioIssue?.errors.length && showErrors
      ? 'ring-2 ring-red-400'
      : ratioIssue?.warnings.length && showWarnings
        ? 'ring-2 ring-amber-400'
        : '';
  if (item.file)
    return (
      <LocalFileThumb
        file={item.file}
        aspect={aspect}
        onRemove={onRemove}
        canRemove={canRemove}
        onPreview={onPreview}
        onDims={reportDims}
        borderHighlight={borderHighlight}
      />
    );
  if (item.saved?.signed_url) {
    const isVideo = !!item.saved.content_type?.startsWith('video/');
    return (
      <SavedThumb
        url={item.saved.signed_url}
        isVideo={isVideo}
        aspect={aspect}
        onRemove={onRemove}
        canRemove={canRemove}
        onPreview={onPreview}
        onDims={reportDims}
        borderHighlight={borderHighlight}
      />
    );
  }
  return null;
}

// ─── Video first-frame extraction (same approach as social ads) ──────────────

function extractVideoFirstFrame(file: File): Promise<{ dataUrl: string; w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;
    video.currentTime = 0.1;
    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      URL.revokeObjectURL(url);
      resolve({
        dataUrl: canvas.toDataURL('image/jpeg', 0.85),
        w: video.videoWidth,
        h: video.videoHeight,
      });
    });
    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Video load failed'));
    });
    video.load();
  });
}

function extractVideoFirstFrameFromUrl(
  url: string,
): Promise<{ dataUrl: string; w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.src = url;

    video.addEventListener('loadeddata', () => {
      video.currentTime = 0.1;
    });
    video.addEventListener('seeked', () => {
      if (!video.videoWidth || !video.videoHeight) {
        reject(new Error('Video dimensions unavailable'));
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/jpeg', 0.85),
        w: video.videoWidth,
        h: video.videoHeight,
      });
    });
    video.addEventListener('error', () => reject(new Error('Video load failed')));
    video.load();
  });
}

// ─── Dimension badge ────────────────────────────────────────────────────────

function formatRatio(r: number): string {
  const common: Record<string, string> = {
    '1.91': '1.91:1',
    '1.78': '16:9',
    '1.72': '1.72:1',
    '1.69': '1.69:1',
    '1.55': '1.55:1',
    '1.5': '3:2',
    '1.33': '4:3',
    '1': '1:1',
    '0.93': '0.93:1',
    '0.8': '4:5',
    '0.75': '3:4',
    '0.67': '2:3',
    '0.56': '9:16',
    '0.5': '1:2',
    '4': '4:1',
    '5': '5:1',
  };
  const key = Number(r.toFixed(2)).toString();
  return common[key] ?? `${r.toFixed(2)}:1`;
}

function DimensionBadge({ width, height }: { width: number; height: number }) {
  const ratio = formatRatio(width / height);
  return (
    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[9px] font-medium bg-black/60 text-white rounded opacity-0 group-hover/img:opacity-100 transition-opacity whitespace-nowrap">
      {width}×{height} · {ratio}
    </span>
  );
}

// ─── Hover action buttons (view + delete) ───────────────────────────────────

function ThumbActions({
  onView,
  onRemove,
  canRemove,
}: {
  onView: () => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  return (
    <>
      <button
        type="button"
        onClick={onView}
        className="absolute top-1 left-1 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center shadow-md opacity-0 group-hover/img:opacity-100 transition-opacity"
      >
        <Eye className="w-3 h-3" />
      </button>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md opacity-0 group-hover/img:opacity-100 transition-opacity"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </>
  );
}

// ─── Fullscreen preview modal ───────────────────────────────────────────────

function MediaPreviewModal({
  src,
  isVideo,
  onClose,
}: {
  src: string;
  isVideo: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        {isVideo ? (
          <video src={src} controls autoPlay className="max-w-full max-h-[90vh] rounded-lg" />
        ) : (
          <img
            src={src}
            alt="Full preview"
            className="max-w-full max-h-[90vh] rounded-lg object-contain"
          />
        )}
      </div>
    </div>
  );
}

// ─── Thumbnail components ───────────────────────────────────────────────────

function SavedThumb({
  url,
  isVideo,
  aspect,
  onRemove,
  canRemove = true,
  onPreview,
  onDims,
  borderHighlight = '',
}: {
  url: string;
  isVideo?: boolean;
  aspect: string;
  onRemove: () => void;
  canRemove?: boolean;
  onPreview: (p: PreviewInfo) => void;
  onDims?: (w: number, h: number) => void;
  borderHighlight?: string;
}) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(isVideo ? null : url);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const reportedRef = useRef(false);

  useEffect(() => {
    if (!isVideo) {
      setThumbUrl(url);
      return;
    }
    reportedRef.current = false;
    let cancelled = false;
    extractVideoFirstFrameFromUrl(url)
      .then((r) => {
        if (cancelled) return;
        setThumbUrl(r.dataUrl);
        setDims({ w: r.w, h: r.h });
        if (!reportedRef.current) {
          reportedRef.current = true;
          onDims?.(r.w, r.h);
        }
      })
      .catch(() => {
        // On failure, use a simple video play icon placeholder instead of the
        // raw video URL (which would break when rendered as <img>).
        if (!cancelled) setThumbUrl(VIDEO_FALLBACK_SENTINEL);
      });
    return () => {
      cancelled = true;
    };
  }, [url, isVideo, onDims]);

  if (!thumbUrl)
    return (
      <div
        className="relative flex-shrink-0 h-24 rounded-lg border border-slate-200 bg-slate-100 animate-pulse"
        style={{ aspectRatio: aspect }}
      />
    );
  if (thumbUrl === VIDEO_FALLBACK_SENTINEL)
    return (
      <div
        className={`relative flex-shrink-0 h-24 group/img rounded-lg ${borderHighlight} cursor-pointer`}
        style={{ aspectRatio: aspect }}
        onClick={() => onPreview({ src: url, isVideo: true })}
      >
        <div className="h-full w-full flex items-center justify-center rounded-lg border border-slate-200 bg-slate-900">
          <Play className="w-8 h-8 text-white opacity-80" />
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 rounded-full w-5 h-5 flex items-center justify-center text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  return (
    <div
      className={`relative flex-shrink-0 h-24 group/img rounded-lg ${borderHighlight}`}
      style={{ aspectRatio: aspect }}
    >
      <img
        src={thumbUrl}
        alt="preview"
        className="h-full w-full object-cover rounded-lg border border-slate-200"
        onLoad={(e) => {
          if (!dims) {
            const img = e.currentTarget;
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            setDims({ w, h });
            if (!reportedRef.current) {
              reportedRef.current = true;
              onDims?.(w, h);
            }
          }
        }}
      />
      {dims && <DimensionBadge width={dims.w} height={dims.h} />}
      <ThumbActions
        onView={() => onPreview({ src: isVideo ? url : thumbUrl, isVideo: !!isVideo })}
        onRemove={onRemove}
        canRemove={canRemove}
      />
    </div>
  );
}

function LocalFileThumb({
  file,
  aspect,
  onRemove,
  canRemove = true,
  onPreview,
  onDims,
  borderHighlight = '',
}: {
  file: File;
  aspect: string;
  onRemove: () => void;
  canRemove?: boolean;
  onPreview: (p: PreviewInfo) => void;
  onDims?: (w: number, h: number) => void;
  borderHighlight?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [rawSrc, setRawSrc] = useState<string | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const isVideo = file.type.startsWith('video/');

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setRawSrc(objectUrl);
    if (isVideo) {
      extractVideoFirstFrame(file)
        .then((r) => {
          setSrc(r.dataUrl);
          setDims({ w: r.w, h: r.h });
          onDims?.(r.w, r.h);
        })
        .catch(() => setSrc(objectUrl));
    } else {
      setSrc(objectUrl);
    }
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, isVideo, onDims]);

  if (!src)
    return (
      <div
        className="relative flex-shrink-0 h-24 rounded-lg border border-slate-200 bg-slate-100 animate-pulse"
        style={{ aspectRatio: aspect }}
      />
    );
  return (
    <div
      className={`relative flex-shrink-0 h-24 group/img rounded-lg ${borderHighlight}`}
      style={{ aspectRatio: aspect }}
    >
      <img
        src={src}
        alt="preview"
        className="h-full w-full object-cover rounded-lg border border-slate-200"
        onLoad={(e) => {
          if (!dims) {
            const img = e.currentTarget;
            const w = img.naturalWidth;
            const h = img.naturalHeight;
            setDims({ w, h });
            onDims?.(w, h);
          }
        }}
      />
      {dims && <DimensionBadge width={dims.w} height={dims.h} />}
      <ThumbActions
        onView={() => onPreview({ src: isVideo ? (rawSrc ?? src) : src, isVideo })}
        onRemove={onRemove}
        canRemove={canRemove}
      />
    </div>
  );
}
